import { useEffect, useMemo, useState } from "react";
import MessageAudioBox from "components/Chat/MessageAudioBox";
import FirebaseMessageList from "./FirebaseMessageList";
import { useAppDispatch, useAppSelector } from "store/store";
import UseGetZoomLevel from "hooks/UseGetZoomLevel";
import Loader from "assets/img/loader_green.gif";
import { getDatabase, ref, onValue } from "firebase/database";

import { markCandidateMessagesAsReadInFirebase } from "./helpers";
import { useSendMessageMutation } from "services/message";

import MessageInput from "./MessageInput";
import { ChatMessageRole, ChatType, SessionScope, firebaseCollections } from "globals/enums";
import { handleError } from "helpers/sentry";
import { participantGroupSessionPath } from "helpers/firebase-path";

type ChatProps = {
  chatOpen?: boolean;
  handleChatOpen: () => void;
  handleChatClose: () => void;
};

type Message = {
  created_at: string;
  id: string;
  read_at?: string;
  received_at: string;
  role?: string;
  text?: string;
  updated_at?: string;
  user_id?: number;
  type?: string;
  url?: string;
  role_to?: ChatMessageRole[];
  alert_type_id?: number;
  metadata?: object;
  image_url?: string;
};

const FirebaseChatView = (Props: ChatProps) => {
  const configuration = useAppSelector((state) => state.app.configuration);
  const alertConfig = useAppSelector((state) => state.app.alertConfigs);
  const dispatch = useAppDispatch();
  const [chatMessages, setChatMessages] = useState<any[]>([]);
  const { resizeLevel } = UseGetZoomLevel();
  const [sendMessage] = useSendMessageMutation();

  const sessionId = useAppSelector((state) => state.app?.session?.session_id);
  const sessionType = useAppSelector((state) => state.app?.session?.session_type);
  const participantGroupId = useAppSelector((state) => state.app.channelData.participant_group_id);
  const sasToken = useAppSelector((state) => state.app?.storage?.sas_token);
  const messageRef = useMemo(
    () =>
      ref(
        getDatabase(),
        sessionType === SessionScope.LiveProctor
          ? participantGroupSessionPath(participantGroupId, sessionId, "messages")
          : `${firebaseCollections.SessionV2}/${sessionId}/messages`
      ),
    [participantGroupId, sessionId, sessionType]
  );

  const filterChatMessages = (messages: Message[]) =>
    messages
      .filter(
        (message: Message) =>
          (message?.type !== ChatType.RECORDING &&
            (!message.role_to || message.role_to?.includes(ChatMessageRole.Candidate))) ||
          message.role === ChatMessageRole.Candidate
      )
      .map((message: Message) => {
        if (message.image_url) {
          message.image_url += `?${sasToken}`;
        }
        return message;
      });

  useEffect(() => {
    if (!sessionId) return;
    if (
      sessionType === SessionScope.LiveProctor &&
      !(typeof participantGroupId === "number" && participantGroupId > 0)
    ) {
      return;
    }
    let processing = false;
    const unsubscribe = onValue(messageRef, async (messageSnapShot) => {
      if (processing) return;
      processing = true;
      try {
        const chatMessages = messageSnapShot.val() || {};
        const messages = filterChatMessages(Object.values(chatMessages));
        setChatMessages(messages);
        const proctorMessages = messages.filter(
          (message) =>
            (message?.role === ChatMessageRole.Proctor || message?.role === ChatMessageRole.Alvy) &&
            !message.read_at
        );
        if (proctorMessages.length) {
          Props.handleChatOpen();
          await markCandidateMessagesAsReadInFirebase(
            sessionId,
            sessionType,
            participantGroupId,
            chatMessages,
            alertConfig,
            dispatch
          );
        }
      } catch (error) {
        handleError(error);
      } finally {
        processing = false;
      }
    });

    return () => unsubscribe();
  }, [sessionId, sessionType, participantGroupId, messageRef, alertConfig, dispatch]);

  const handleSendMessage = async (inputText?: string, inputFile?: string) => {
    if (!inputText?.trim()) return;

    try {
      const messageData = {
        role: ChatMessageRole.Candidate,
        text: inputText,
        image_file: inputFile,
        role_to: [ChatMessageRole.Alvy, ChatMessageRole.Proctor]
      };
      await sendMessage(messageData).unwrap();
    } catch (error) {
      handleError(error);
    }
  };

  return (
    <>
      {Props.chatOpen && (
        <div
          className={resizeLevel >= 11 ? "tvp-resizeChatScreen" : "tvp-chatScreen"}
          data-testid="chat-screen"
          aria-live="polite"
        >
          <div className="tvp-chatHeader" aria-labelledby="chat-heading" role="dialog">
            <h2 id="chat-heading">Talk to Proctor</h2>
            <div className="tvp-chatButtonWrapper">
              {" "}
              {configuration.audio_call && configuration.audio_check && <MessageAudioBox />}
              <button onClick={Props.handleChatClose} className="tvp-closeChat" aria-label="Close chat">x</button>
            </div>
          </div>

          {chatMessages ? (
            chatMessages?.length ? (
              <FirebaseMessageList chatMessages={chatMessages} />
            ) : (
              <div className="tvp-flex tvp-flex-col tvp-flex-grow"></div>
            )
          ) : (
            <img className="tvp-miniLoader" src={Loader} alt="loader" />
          )}
          <MessageInput handleSendMessage={handleSendMessage} active={true} />
        </div>
      )}
    </>
  );
};

export default FirebaseChatView;
